import * as R from "ramda";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { CLEANING_RATES, WATER_USAGES } from "../../computations/contants";
import SliderPanel from "../../sliderPanel/sliderPanel";
import RightPanel from "../rightPanel/rightPanel";
import styles from "./mainView.module.scss";

const Container = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    "& > *": {
      maxWidth: "960px",
    },
  },
  [theme.breakpoints.up("lg")]: {
    "& > *": {
      maxWidth: "1150px",
    },
  },
}));

function MainView({ mopType, currency, surfaceUnit }) {
  const { i18n } = useTranslation(["common"]);
  const [areaSize, setAreaSize] = useState(400);
  const [cleanedPerWeek, setCleanedPerWeek] = useState(5);
  const [labourCost, setLabourCost] = useState(30);
  const [chemicalDilutionRate, setChemicalDilutionRate] = useState(100);
  const [costOfChemicals, setCostOfChemicals] = useState(5);
  const [maxCost, setMaxCost] = useState(100);

  useEffect(() => {
    switch (i18n.resolvedLanguage) {
      case 'pl':
        setMaxCost(500);
        break;
      case 'dk':
      case 'no':
      case 'sw':
        setMaxCost(1000);
        break;
      case 'cz':
        setMaxCost(2000);
        break;
      case 'jp':
        setMaxCost(15000);
        break;
      case 'hu':
        setMaxCost(30000);
        break;
      default:
        setMaxCost(100);
    }
  }, [i18n.resolvedLanguage]);

  const sliders = {
    areaSize,
    setAreaSize,
    cleanedPerWeek,
    setCleanedPerWeek,
    labourCost,
    setLabourCost,
    chemicalDilutionRate,
    setChemicalDilutionRate,
    costOfChemicals,
    setCostOfChemicals,
    maxCost,
  };

  const mopsCleaningTimes = useMemo(
    () =>
      R.fromPairs(
        R.toPairs(CLEANING_RATES).map(([type, rate]) => [
          type,
          Math.round((60 * (areaSize / surfaceUnit.conversionRatio)) / rate),
        ])
      ),
    [areaSize, surfaceUnit]
  );

  const mopsLabourCosts = useMemo(
    () =>
      R.fromPairs(
        R.toPairs(mopsCleaningTimes).map(([type, time]) => [
          type,
          Math.round((labourCost * time) / 60),
        ])
      ),
    [mopsCleaningTimes, labourCost]
  );

  const mopsYearlyCleaningTimes = useMemo(
    () =>
      R.fromPairs(
        R.toPairs(mopsCleaningTimes).map(([type, time]) => [
          type,
          Math.round(52 * cleanedPerWeek * time),
        ])
      ),
    [mopsCleaningTimes, cleanedPerWeek]
  );

  const mopsYearlyLabourCosts = useMemo(
    () =>
      R.fromPairs(
        R.toPairs(mopsYearlyCleaningTimes).map(([type, time]) => [
          type,
          Math.round((labourCost * time) / 60),
        ])
      ),
    [mopsYearlyCleaningTimes, labourCost]
  );

  const mopsYearlyChemicalCosts = useMemo(
    () =>
      R.fromPairs(
        R.toPairs(WATER_USAGES).map(([type, waterUsage]) => {
          const waterUsed =
            (waterUsage * (areaSize / surfaceUnit.conversionRatio)) / 100;
          const chemicalUsed = waterUsed / (chemicalDilutionRate || 1);
          return [type, Math.round(chemicalUsed * costOfChemicals)];
        })
      ),
    [areaSize, chemicalDilutionRate, costOfChemicals, surfaceUnit]
  );

  return (
    <Container>
      <Grid
        container
        justifyContent="center"
        spacing={0}
        direction={{ xs: "column", sm: "row" }}
        className={styles.mainContainer}
      >
        <Grid item container xs={12} sm={6}>
          <SliderPanel
            sliders={sliders}
            currency={currency}
            surfaceUnit={surfaceUnit}
          />
        </Grid>
        <Grid item container xs={12} sm={6}>
          <RightPanel
            currency={currency}
            surfaceUnit={surfaceUnit}
            mopType={mopType}
            computations={{
              mopsCleaningTimes,
              mopsLabourCosts,
              mopsYearlyCleaningTimes,
              mopsYearlyLabourCosts,
              mopsYearlyChemicalCosts,
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default MainView;
